import { ConditionalDisplay } from "@/models/new/forms.types"
import { FormResponse } from "@/models/new/formResponse.types"

export type ConditionValueAliases = Record<string, Record<string, string>>

/**
 * Evaluates whether an element (field or section) should be visible based on conditional display logic
 * @param conditionalDisplay - The conditional display configuration from the field/section
 * @param formResponse - The current form response containing all field values
 * @param instanceContext - Optional context for dynamic group fields (groupId and instance number)
 * @param conditionValueAliases - Optional legacy condition code to canonical ID mappings by trigger
 * @returns true if the element should be visible, false otherwise
 */
export const shouldShowElement = (
  conditionalDisplay: ConditionalDisplay | undefined,
  formResponse: FormResponse,
  instanceContext?: { groupId: string; instance: number; fields?: { id: string; name: string }[] },
  conditionValueAliases: ConditionValueAliases = {},
): boolean => {
  if (!conditionalDisplay) {
    return true
  }

  const { triggerField, conditions } = conditionalDisplay
  const triggerValueAliases = Object.prototype.hasOwnProperty.call(
    conditionValueAliases,
    triggerField,
  )
    ? conditionValueAliases[triggerField]
    : {}

  // For fields within dynamic groups, check instance-scoped trigger first
  let triggerResponse
  if (instanceContext) {
    const triggerName =
      instanceContext.fields?.find((field) => field.id === triggerField)?.name ?? triggerField
    const instanceKey = `${instanceContext.groupId}.${instanceContext.instance}.${triggerName}`
    triggerResponse = formResponse.responses[instanceKey] || formResponse.responses[triggerField]
  } else {
    triggerResponse = formResponse.responses[triggerField]
  }

  if (!triggerResponse) {
    return false
  }

  // Get the actual value from the trigger field response
  let triggerValue: string | string[] | undefined

  if (triggerResponse.type === "select") {
    triggerValue = triggerResponse.value
  } else if (triggerResponse.type === "boolean") {
    triggerValue = triggerResponse.value
  } else if (triggerResponse.type === "text" || triggerResponse.type === "paragraph") {
    triggerValue = triggerResponse.value
  } else {
    // For other types, convert to string
    triggerValue = String(triggerResponse.value)
  }

  // Evaluate all conditions (AND logic - all must be true)
  return conditions.every((condition) => {
    const { operator, value: conditionValue } = condition
    const canonicalValue = (value: string) =>
      triggerResponse.type === "select" &&
      Object.prototype.hasOwnProperty.call(triggerValueAliases, value)
        ? triggerValueAliases[value]
        : value
    const canonicalConditionValue = Array.isArray(conditionValue)
      ? conditionValue.map(canonicalValue)
      : canonicalValue(conditionValue)

    switch (operator) {
      case "equals":
        if (Array.isArray(triggerValue)) {
          return (
            !Array.isArray(canonicalConditionValue) &&
            triggerValue.includes(canonicalConditionValue)
          )
        }
        return triggerValue === canonicalConditionValue

      case "isOneOf": {
        if (!Array.isArray(canonicalConditionValue)) return false
        if (Array.isArray(triggerValue)) {
          return triggerValue.some((v) => canonicalConditionValue.includes(v))
        }
        return canonicalConditionValue.includes(triggerValue as string)
      }

      case "isNot":
        if (Array.isArray(triggerValue)) {
          return (
            Array.isArray(canonicalConditionValue) ||
            !triggerValue.includes(canonicalConditionValue)
          )
        }
        return triggerValue !== canonicalConditionValue

      default:
        return false
    }
  })
}
